import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import '../assets/UserMenu.css';

const UserMenu = ({ user, onLogout, onNavigate, showActivityLog = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef(null);
  const navigate = useNavigate();

  // Click-outside detection and keyboard handling
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === 'Escape' && isOpen) {
        setIsOpen(false);
        // Return focus to trigger button
        menuRef.current?.querySelector('.user-menu-trigger')?.focus();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  const toggleMenu = () => {
    setIsOpen(!isOpen);
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      toggleMenu();
    }
  };

  const handleMenuItemKeyDown = (event, action) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      action();
    }
  };

  const handleMenuItemClick = (action, path) => {
    setIsOpen(false);
    
    if (action === 'logout') {
      onLogout();
    } else if (path) {
      if (onNavigate) {
        onNavigate(path);
      } else {
        navigate(path);
      }
    }
  };

  const getIcon = (iconName) => {
    const icons = {
      user: (
        <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
          <path d="M8 8a3 3 0 1 0 0-6 3 3 0 0 0 0 6zm2-3a2 2 0 1 1-4 0 2 2 0 0 1 4 0zm4 8c0 1-1 1-1 1H3s-1 0-1-1 1-4 6-4 6 3 6 4zm-1-.004c-.001-.246-.154-.986-.832-1.664C11.516 10.68 10.289 10 8 10c-2.29 0-3.516.68-4.168 1.332-.678.678-.83 1.418-.832 1.664h10z"/>
        </svg>
      ),
      clipboard: (
        <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
          <path d="M4 1.5H3a2 2 0 0 0-2 2V14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V3.5a2 2 0 0 0-2-2h-1v1h1a1 1 0 0 1 1 1V14a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V3.5a1 1 0 0 1 1-1h1v-1z"/>
          <path d="M9.5 1a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5h-3a.5.5 0 0 1-.5-.5v-1a.5.5 0 0 1 .5-.5h3zm-3-1A1.5 1.5 0 0 0 5 1.5v1A1.5 1.5 0 0 0 6.5 4h3A1.5 1.5 0 0 0 11 2.5v-1A1.5 1.5 0 0 0 9.5 0h-3z"/>
        </svg>
      ),
      logout: (
        <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
          <path fillRule="evenodd" d="M10 12.5a.5.5 0 0 1-.5.5h-8a.5.5 0 0 1-.5-.5v-9a.5.5 0 0 1 .5-.5h8a.5.5 0 0 1 .5.5v2a.5.5 0 0 0 1 0v-2A1.5 1.5 0 0 0 9.5 2h-8A1.5 1.5 0 0 0 0 3.5v9A1.5 1.5 0 0 0 1.5 14h8a1.5 1.5 0 0 0 1.5-1.5v-2a.5.5 0 0 0-1 0v2z"/>
          <path fillRule="evenodd" d="M15.854 8.354a.5.5 0 0 0 0-.708l-3-3a.5.5 0 0 0-.708.708L14.293 7.5H5.5a.5.5 0 0 0 0 1h8.793l-2.147 2.146a.5.5 0 0 0 .708.708l3-3z"/>
        </svg>
      )
    };
    return icons[iconName];
  };

  const menuItems = [
    {
      id: 'profile',
      label: 'Profile',
      icon: 'user',
      action: () => handleMenuItemClick('navigate', user?.role === 'tutor' ? '/tutor/profile' : '/admin/profile'),
      visible: true
    },
    {
      id: 'activity-log',
      label: 'Activity Log',
      icon: 'clipboard',
      action: () => handleMenuItemClick('navigate', '/admin/activity'),
      visible: showActivityLog,
      divider: true
    },
    {
      id: 'logout',
      label: 'Logout',
      icon: 'logout',
      action: () => handleMenuItemClick('logout'),
      visible: true
    }
  ];

  return (
    <div className="user-menu" ref={menuRef}>
      <button 
        className="user-menu-trigger"
        onClick={toggleMenu}
        onKeyDown={handleKeyDown}
        aria-expanded={isOpen}
        aria-haspopup="true"
        aria-label={`User menu for ${user?.name}`}
      >
        <span className="user-avatar" aria-hidden="true">
          <svg width="20" height="20" viewBox="0 0 16 16" fill="currentColor">
            <path d="M8 8a3 3 0 1 0 0-6 3 3 0 0 0 0 6zm2-3a2 2 0 1 1-4 0 2 2 0 0 1 4 0zm4 8c0 1-1 1-1 1H3s-1 0-1-1 1-4 6-4 6 3 6 4zm-1-.004c-.001-.246-.154-.986-.832-1.664C11.516 10.68 10.289 10 8 10c-2.29 0-3.516.68-4.168 1.332-.678.678-.83 1.418-.832 1.664h10z"/>
          </svg>
        </span>
        <span className="user-name">{user?.name}</span>
        <span className={`dropdown-arrow ${isOpen ? 'open' : ''}`} aria-hidden="true">
          <svg width="12" height="12" viewBox="0 0 16 16" fill="currentColor">
            <path fillRule="evenodd" d="M1.646 4.646a.5.5 0 0 1 .708 0L8 10.293l5.646-5.647a.5.5 0 0 1 .708.708l-6 6a.5.5 0 0 1-.708 0l-6-6a.5.5 0 0 1 0-.708z"/>
          </svg>
        </span>
      </button>

      {isOpen && (
        <div 
          className="user-menu-dropdown"
          role="menu"
          aria-label="User menu options"
        >
          <div className="user-menu-header">
            <div className="user-info">
              <div className="user-info-name">{user?.name}</div>
              <div className="user-info-role">
                {user?.role === 'admin' ? 'Administrator' : 
                 user?.role === 'finance' ? 'Finance' : 
                 user?.role === 'tutor' ? 'Tutor' : user?.role}
              </div>
            </div>
          </div>
          
          <div className="user-menu-items">
            {menuItems
              .filter(item => item.visible)
              .map((item, index) => (
                <React.Fragment key={item.id}>
                  <button
                    className="user-menu-item"
                    onClick={item.action}
                    onKeyDown={(e) => handleMenuItemKeyDown(e, item.action)}
                    data-testid={`menu-item-${item.id}`}
                    role="menuitem"
                    aria-label={item.label}
                  >
                    <span className="menu-item-icon" aria-hidden="true">{getIcon(item.icon)}</span>
                    <span className="menu-item-label">{item.label}</span>
                  </button>
                  {item.divider && <div className="menu-divider" role="separator" aria-hidden="true" />}
                </React.Fragment>
              ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default UserMenu;
